export type CountryInfo = { code: string; name: string; flag: string }

// Canonical names to avoid SSR/CSR Intl discrepancies
const CANONICAL_REGION_NAMES: Record<string, string> = {
  HK: "Hong Kong",
  MO: "Macao",
};

export function countryCodeToFlagEmoji(code: string): string {
  const upper = code.trim().toUpperCase()
  if (!/^[A-Z]{2}$/.test(upper)) return "🏳️"
  const OFFSET = 127397
  return String.fromCodePoint(upper.charCodeAt(0) + OFFSET) + String.fromCodePoint(upper.charCodeAt(1) + OFFSET)
}

// Cache one DisplayNames instance per locale — the constructor loads locale
// data tables that are expensive to rebuild on every call.
const regionDisplayNamesCache = new Map<string, Intl.DisplayNames>()

function getRegionDisplayNames(locale: string): Intl.DisplayNames {
  let dn = regionDisplayNamesCache.get(locale)
  if (!dn) {
    // @ts-ignore - TS may not have types for newer Intl APIs in some envs
    // FP: instance is memoized per-locale in regionDisplayNamesCache
    // oxlint-disable-next-line react-doctor/js-hoist-intl
    dn = new Intl.DisplayNames([locale], { type: "region" })
    regionDisplayNamesCache.set(locale, dn)
  }
  return dn
}

export function getCountryName(code: string, locale = "en-US"): string | undefined {
  try {
    const dn = getRegionDisplayNames(locale)
    const upper = code.toUpperCase()
    return CANONICAL_REGION_NAMES[upper] || dn.of(upper) || undefined
  } catch {
    return undefined
  }
}

function listCountryCodes(): string[] {
  try {
    // @ts-ignore - supportedValuesOf may not exist in older runtimes
    const regions: string[] = Intl.supportedValuesOf?.("region") || []
    return regions.filter((r) => /^[A-Z]{2}$/.test(r))
  } catch {
    // Fallback minimal set if runtime lacks the API; can be expanded if needed
    return [
      "US","GB","FR","DE","BR","CA","AU","ES","IT","JP","CN","KR","IN","MX","NL","SE","NO","DK","FI","CH","IE","PT","PL","CZ","AT","BE","LU","GR","HU","RO","BG","SK","SI","HR","EE","LV","LT","IS","TR","AE","SA","EG","ZA","AR","CL","CO","PE","UY","NZ","SG","HK","TW","TH","VN","PH","MY","ID","PK","BD","NG","KE","MA","TN","IL","RU","UA"
    ]
  }
}

export function getAllCountries(locale = "en-US"): CountryInfo[] {
  const codes = listCountryCodes()
  return codes
    .map((code) => {
      const upper = code.toUpperCase()
      const name = CANONICAL_REGION_NAMES[upper] || getCountryName(upper, locale) || upper
      const flag = countryCodeToFlagEmoji(code)
      return { code: code.toLowerCase(), name, flag }
    })
    .sort((a, b) => a.name.localeCompare(b.name))
}


